import { ContentSource } from 'rwl-shared/dist/models/ContentSource';
import {
    FeaturedJob,
    FeaturedMedia,
    FeaturedReview,
} from 'rwl-shared/dist/models/neighborhood';
import { MediaType } from 'rwl-shared/dist/utils/media';
import { V2TopJob, V2TopJobMedia, V2TopReview } from './v2NeighborhoodTypes';

/**
 * Adapters from the v2 widget service's `serviceArea/content` (and the
 * neighborhood response's embedded) job/review shapes to the `FeaturedJob` /
 * `FeaturedReview` shapes this widget's pop-out already consumes, so the
 * migration is confined to the fetchers and no component changes (the same
 * approach `adaptV2Neighborhood` takes for the map side).
 *
 * Forward-compatible edges — v2 does not serve these yet (they await the
 * EN-1328 deferred aggregate re-bake), so the maps below no-op today and the
 * widget's `When`-guarded panes stay hidden. They are mapped through on presence
 * so the re-bake lights them up with NO further app deploy:
 * - `FeaturedReview.response` (owner reply) and `FeaturedReview.jobId` — the
 *   owner-response pane and review→job link.
 * - `FeaturedJob.reviewSource`/`reviewRating` — the job-card review badge (v2
 *   stores only `reviewId` on the job today).
 *
 * Genuinely dropped (unused by the pop-out): `FeaturedJob.showcase`/`labels`/
 * `reviewMessages` — defaulted rather than sourced.
 */

/**
 * v2 splits the cover image out of `additionalMedia` (it is served separately as
 * `coverImageUrl`), whereas v1's `media` array carried the cover inline, flagged
 * `isCover` and sorted first. `sortedMedia` (the gallery's sort) keys on
 * `isCover` then `sortOrder`, so we rebuild that: a synthetic cover entry first
 * (`sortOrder: -1`), then the additional media in the order v2 already sorted
 * them (their `sortOrder` was applied at bake time and dropped, so index
 * preserves it).
 */
const adaptV2Media = (
    media: V2TopJobMedia,
    sortOrder: number,
    isCover = false
): FeaturedMedia => ({
    id: media.id,
    // v2 serves the media type as a raw string; translate it to the MediaType
    // the gallery keys on. The gallery renders anything that is not
    // MediaType.Photo as a <video>, so a blind pass-through of a non-"video"
    // value (e.g. "image") would show every photo as a broken video. Mirrors
    // rwl-plugin's v2DataUtils media mapping ("video" → Video, else Photo).
    type: media.type === 'video' ? MediaType.Video : MediaType.Photo,
    url: media.url,
    thumbnailUrl: media.thumbnailUrl,
    // v2 carries no separate raw source URL; the served `url` is the full
    // media. Nothing in the pop-out reads `sourceUrl` distinctly from `url`.
    sourceUrl: media.url,
    caption: media.caption,
    sortOrder,
    isCover,
    // geolocation/postedAt are optional on FeaturedMedia and have no v2
    // counterpart on a content-media entry — the pop-out gallery reads neither.
});

/**
 * Build the pop-out's media gallery from v2's `coverImageUrl` + `additionalMedia`.
 *
 * Only synthesizes the cover entry when the job actually has media: a job with
 * no photos gets `coverImageUrl` = the placeholder, and surfacing that as a
 * gallery item would show a placeholder where v1 showed an empty gallery.
 */
const buildFeaturedMedia = (job: V2TopJob): FeaturedMedia[] => {
    const additional = job.additionalMedia.map((m, i) => adaptV2Media(m, i));
    const hasMedia =
        job.additionalMedia.length > 0 || job.additionalMediaCount > 0;
    if (hasMedia && job.coverImageUrl) {
        return [
            adaptV2Media(
                {
                    id: `${job.id}-cover`,
                    type: 'image',
                    url: job.coverImageUrl,
                    thumbnailUrl: job.coverImageUrl,
                    caption: '',
                },
                -1,
                true
            ),
            ...additional,
        ];
    }
    return additional;
};

export const adaptV2FeaturedJob = (job: V2TopJob): FeaturedJob => ({
    id: job.id,
    // v2 omits geolocation for jobs that never resolved to a coordinate. v1's
    // serializer emitted 0/0 for those; nothing in the pop-out reads a job's
    // geolocation (only the map does, via the separately-adapted NeighborhoodJob),
    // so the fallback is inert.
    geolocation: {
        latitude: job.geolocation?.lat ?? 0,
        longitude: job.geolocation?.lng ?? 0,
    },
    media: buildFeaturedMedia(job),
    ...(job.locationInfo
        ? {
              locationInfo: {
                  city: job.locationInfo.city,
                  state: job.locationInfo.state,
                  zipCode: job.locationInfo.zip,
              },
          }
        : {}),
    ...(job.startedAt ? { startedAt: new Date(job.startedAt) } : {}),
    ...(job.completedAt ? { completedAt: new Date(job.completedAt) } : {}),
    ...(job.title !== undefined ? { title: job.title } : {}),
    ...(job.description !== undefined ? { description: job.description } : {}),
    ...(job.reviewId !== undefined ? { reviewId: job.reviewId } : {}),
    // Job-card review badge: v2 stores only reviewId today (see the module
    // header), so these guards no-op. Mapped through the moment the service
    // surfaces the linked review's source/rating — no further app change.
    ...(job.reviewSource !== undefined
        ? { reviewSource: job.reviewSource as ContentSource }
        : {}),
    ...(job.reviewRating !== undefined
        ? { reviewRating: job.reviewRating }
        : {}),
    labels: [],
});

export const adaptV2FeaturedReview = (review: V2TopReview): FeaturedReview => ({
    id: review.id,
    // v1 exposed the reviewer name as `author`; v2 serves it (already
    // privacy-simplified server-side) as `authorName`.
    author: review.authorName,
    // v1 `rating` is the numeric star value; v2 carries both a display `rating`
    // string and the numeric `ratingNum` — the sort and stars want the number.
    rating: review.ratingNum ?? 0,
    message: review.message ?? '',
    // Fall back start→updated→epoch so `sortReviews` (getSafeTimestamp) and the
    // date display always have a Date; v2 reviews virtually always carry postedAt.
    postedAt: review.postedAt
        ? new Date(review.postedAt)
        : review.updatedAt
        ? new Date(review.updatedAt)
        : new Date(0),
    // v2 source is a raw string (e.g. "google"/"Internal"); cast to the enum and
    // fall back to Internal. Source-icon casing is an accepted minor gap.
    source: (review.source as ContentSource) ?? ContentSource.Internal,
    ...(review.authorPhotoUrl ? { authorPhotoUrl: review.authorPhotoUrl } : {}),
    ...(review.updatedAt ? { updatedAt: new Date(review.updatedAt) } : {}),
    // Owner-response pane and review→job link: v2 does not serve these yet (see
    // the module header), so the guards no-op today. Mapped through the moment
    // the deferred re-bake starts populating them — no further app change.
    ...(review.response
        ? {
              response: {
                  author: review.response.author ?? '',
                  message: review.response.message ?? '',
                  postedAt: review.response.postedAt
                      ? new Date(review.response.postedAt)
                      : new Date(0),
              },
          }
        : {}),
    ...(review.jobId !== undefined ? { jobId: review.jobId } : {}),
});
